/**
 * Created by piotr.pozniak@thebeaverhead.com on 07/02/2023.
 *
 * Untitled UI restyle — moved onto the shared QueueTabsPageTemplate (same
 * shell QueueJobsListPage uses), replacing the hand-rolled page chrome that
 * used to duplicate ~80% of that template. One genuine behavior change:
 * starting a new worker now shows a confirmation toast and refetches the
 * list (previously silent — the original had a commented-out refetch that
 * was never wired up).
 */
import React, { useCallback, useEffect, useMemo } from "react";
import Button from "../theme/untitled/Button/Button";
import DropdownMenu from "../theme/untitled/DropdownMenu/DropdownMenu";
import Card from "../theme/untitled/Card/Card";
import Icon from "../theme/untitled/icons/Icon";
import { ToastProvider, useToast } from "../theme/untitled/Toast/ToastProvider";
import QueueTabsPageTemplate from "../templates/QueueTabsPageTemplate/QueueTabsPageTemplate";
import QueueWorkersTable from "./QueueWorkersPage/QueueWorkersTable";
import { useQueueWorkersStore } from "../../../hooks/redux/queueWorkers";
import { useAppStore } from "../../../hooks/redux/app";
import styles from "./QueueWorkersPage/QueueWorkersPage.module.scss";

const WORKER_TYPES = [
  { key: "Integration", label: "Sync jobs" },
  { key: "SyncOne", label: "SyncOne jobs" },
  { key: "Email", label: "Email jobs" },
  { key: "ICSUpdate", label: "Update ICS" },
  { key: "ICSDelete", label: "Clean ICS" },
];

// useToast() needs a ToastProvider ancestor, which can't be the same
// component instance that calls the hook — same split as UsersPage.
const QueueWorkersPageContent = () => {
  const showToast = useToast();
  const { queueWorkers, fetchQueueWorkers, runWorker, terminateWorker } =
    useQueueWorkersStore();
  const { clearErrors } = useAppStore();

  useEffect(() => {
    clearErrors();
    fetchQueueWorkers();
    return () => clearErrors();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loading =
    queueWorkers.fetch || queueWorkers.run || queueWorkers.terminate;

  const onStartNewWorker = useCallback(
    async (jobType, label) => {
      await runWorker(jobType);
      showToast({
        variant: "success",
        message: `Started a new ${label} worker.`,
      });
      fetchQueueWorkers();
    },
    [runWorker, fetchQueueWorkers, showToast]
  );

  const onTerminateWorker = useCallback(
    (id) => terminateWorker(id),
    [terminateWorker]
  );

  const onRefresh = useCallback(() => fetchQueueWorkers(), [fetchQueueWorkers]);

  const startWorkerItems = useMemo(
    () =>
      WORKER_TYPES.map((type) => ({
        key: type.key,
        label: type.label,
        onClick: () => onStartNewWorker(type.key, type.label),
      })),
    [onStartNewWorker]
  );

  const startWorkerTrigger = (
    <Button
      hierarchy="primary"
      size="sm"
      iconLeading={<Icon name="Plus" />}
      disabled={loading}
    >
      Start new worker
    </Button>
  );

  const actions = (
    <>
      <DropdownMenu
        trigger={startWorkerTrigger}
        items={startWorkerItems}
        align="end"
      />
      <Button
        hierarchy="secondaryGray"
        size="sm"
        iconLeading={<Icon name="RefreshCcw01" />}
        onClick={onRefresh}
        loading={loading}
      >
        Refresh
      </Button>
    </>
  );

  const totalCount = queueWorkers.collection.length;
  const workerCountLabel = `${totalCount} ${
    totalCount === 1 ? "worker" : "workers"
  }`;

  const content = totalCount ? (
    <QueueWorkersTable
      workers={queueWorkers.collection}
      rowsDisabled={loading}
      onTerminate={onTerminateWorker}
    />
  ) : (
    <div className={styles.empty}>
      <p className={styles.emptyText}>No workers running right now.</p>
    </div>
  );

  return (
    <QueueTabsPageTemplate title="Queue workers">
      <Card>
        <Card.Header>
          <div className={styles.headerActions}>{actions}</div>
        </Card.Header>
        {content}
        <Card.Footer>
          <span className={styles.footerCount}>{workerCountLabel}</span>
        </Card.Footer>
      </Card>
    </QueueTabsPageTemplate>
  );
};

const QueueWorkersPage = () => (
  <ToastProvider>
    <QueueWorkersPageContent />
  </ToastProvider>
);

export default QueueWorkersPage;
